import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { faqsAPI } from '../../services/api';
import { toast } from 'sonner';

const FAQAdmin = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const data = await faqsAPI.getAll();
      setSections(data);
    } catch (e) {
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateSectionField = (id, field, value) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const updateItem = (sectionId, idx, field, value) => {
    setSections((prev) => prev.map((s) => {
      if (s.id !== sectionId) return s;
      const items = [...s.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...s, items };
    }));
  };

  const addItem = (sectionId) => {
    setSections((prev) => prev.map((s) => (
      s.id === sectionId ? { ...s, items: [...s.items, { question: '', answer: '' }] } : s
    )));
  };

  const removeItem = (sectionId, idx) => {
    setSections((prev) => prev.map((s) => {
      if (s.id !== sectionId) return s;
      return { ...s, items: s.items.filter((_, i) => i !== idx) };
    }));
  };

  const saveSection = async (s) => {
    setSaving((p) => ({ ...p, [s.id]: true }));
    try {
      const { id, ...payload } = s;
      await faqsAPI.update(id, payload);
      toast.success(`"${s.title}" saved`);
    } catch (e) {
      toast.error('Failed to save section');
    } finally {
      setSaving((p) => ({ ...p, [s.id]: false }));
    }
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Delete this FAQ section?')) return;
    try {
      await faqsAPI.delete(id);
      toast.success('Section deleted');
      load();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const addSection = async () => {
    try {
      const order = (sections[sections.length - 1]?.order ?? -1) + 1;
      const created = await faqsAPI.create({
        title: 'New Section',
        icon: 'HelpCircle',
        order,
        items: [{ question: '', answer: '' }],
      });
      setSections((p) => [...p, created]);
      setExpanded((p) => ({ ...p, [created.id]: true }));
      toast.success('Section added');
    } catch (e) {
      toast.error('Failed to add section');
    }
  };

  return (
    <div className="space-y-4" data-testid="faq-admin">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold">FAQ Content</h2>
          <p className="text-sm text-gray-500">Manage FAQ sections and individual Q&amp;A entries shown on the public FAQ pages.</p>
        </div>
        <Button onClick={addSection} data-testid="add-section-btn"><Plus className="h-4 w-4 mr-2" /> Add Section</Button>
      </div>

      {loading ? (
        <p className="text-gray-500 text-center py-10">Loading…</p>
      ) : sections.length === 0 ? (
        <p className="text-gray-500 text-center py-10">No FAQ sections yet.</p>
      ) : (
        sections.map((s) => {
          const isOpen = !!expanded[s.id];
          return (
            <Card key={s.id} data-testid={`faq-section-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setExpanded((p) => ({ ...p, [s.id]: !isOpen }))}
                    className="p-1 hover:bg-gray-100 rounded"
                    aria-label="Expand"
                  >
                    {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  <Input
                    value={s.title}
                    onChange={(e) => updateSectionField(s.id, 'title', e.target.value)}
                    className="font-semibold flex-1 min-w-[150px]"
                  />
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>Order:</span>
                    <Input
                      type="number"
                      value={s.order}
                      onChange={(e) => updateSectionField(s.id, 'order', Number(e.target.value))}
                      className="w-16 h-8"
                    />
                  </div>
                  <span className="text-xs text-gray-500">{s.items?.length || 0} Q&amp;A</span>
                  <Button size="sm" onClick={() => saveSection(s)} disabled={saving[s.id]} data-testid={`save-section-${s.id}`}>
                    <Save className="h-3 w-3 mr-1" /> {saving[s.id] ? 'Saving…' : 'Save'}
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => deleteSection(s.id)} data-testid={`delete-section-${s.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>

                {isOpen && (
                  <div className="mt-4 space-y-3 border-t pt-4">
                    {(s.items || []).map((item, idx) => (
                      <div key={idx} className="bg-gray-50 p-3 rounded space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-gray-500">Q&amp;A #{idx + 1}</span>
                          <button
                            onClick={() => removeItem(s.id, idx)}
                            className="text-red-500 hover:bg-red-50 p-1 rounded"
                            aria-label="Remove"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                        <Input
                          placeholder="Question"
                          value={item.question}
                          onChange={(e) => updateItem(s.id, idx, 'question', e.target.value)}
                        />
                        <textarea
                          placeholder="Answer"
                          value={item.answer}
                          onChange={(e) => updateItem(s.id, idx, 'answer', e.target.value)}
                          rows={3}
                          className="w-full p-2 border rounded text-sm"
                        />
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => addItem(s.id)} data-testid={`add-item-${s.id}`}>
                      <Plus className="h-3 w-3 mr-1" /> Add Q&amp;A
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
};

export default FAQAdmin;
